/**
 * Live Trading V3 — hotkey/one-click acknowledgement timing telemetry.
 *
 * The parity requirement is measurable, not vibes: every intent submission
 * records input→guard→local-ack→request→response stamps tagged by input
 * method, in a bounded in-session ring buffer. A controlled parity session
 * reads the summary (P50/P95 per input method per interval) from
 * `window.__lt2Timing` alongside the screen recording; nothing here leaves
 * the browser, polls, or renders.
 *
 * Stamp meanings:
 *   inputAtMs   — the DOM event that started the intent (keydown / click).
 *   guardMs     — local preflight passed; the intent is valid.
 *   ackMs       — the local acknowledgement was presented (pending ack +
 *                 acknowledgement tone). The parity contract is
 *                 ackMs − inputAtMs ≈ 0 for every input method.
 *   requestMs   — the order POST left the store.
 *   responseMs  — the relay answered (venue outcome known).
 *   renderMs    — the first animation frame AFTER the entry's FIRST
 *                 user-visible acknowledgement committed: the pending ack
 *                 for submissions (recorded BEFORE the relay round trip),
 *                 the refusal ack for refused/throttled inputs. The
 *                 paint-adjacent moment the trader could first see a
 *                 response to their input. Honest bound, not compositor
 *                 truth.
 *   finalMs     — the entry's terminal moment, with finalOutcome naming it:
 *                 refusals/throttles are final at their ack; a rejection or
 *                 unknown is final at the response; an accepted resting
 *                 order is final when the visibility reconciliation settles
 *                 (verified on the book, or flipped to unverified); an
 *                 accepted taker is final at the response (no rest to
 *                 verify).
 */

export interface Lt2TimingEntry {
  inputMethod: string;
  /** 'pending' = the submission's local ack is on screen and the relay has
   *  not answered yet; patched to a terminal outcome by updateLt2Timing. */
  outcome: 'pending' | 'accepted' | 'rejected' | 'unverified' | 'throttled' | 'guard-refused';
  inputAtMs: number | null;
  guardMs: number;
  ackMs: number;
  requestMs: number | null;
  responseMs: number | null;
  renderMs: number | null;
  finalMs: number | null;
  finalOutcome: string | null;
}

const MAX_ENTRIES = 200;
const _entries: Lt2TimingEntry[] = [];
let _entrySeq = 0;
const _entryIds = new WeakMap<Lt2TimingEntry, number>();
const _byId = new Map<number, Lt2TimingEntry>();

export function recordLt2Timing(
  entry: Omit<Lt2TimingEntry, 'renderMs' | 'finalMs' | 'finalOutcome'> &
    Partial<Pick<Lt2TimingEntry, 'renderMs' | 'finalMs' | 'finalOutcome'>>,
): number {
  const full: Lt2TimingEntry = {
    renderMs: null,
    finalMs: null,
    finalOutcome: null,
    ...entry,
  };
  const id = ++_entrySeq;
  _entries.push(full);
  _entryIds.set(full, id);
  _byId.set(id, full);
  if (_entries.length > MAX_ENTRIES) {
    for (const dropped of _entries.splice(0, _entries.length - MAX_ENTRIES)) {
      const droppedId = _entryIds.get(dropped);
      if (droppedId != null) _byId.delete(droppedId);
    }
  }
  // The render stamp is self-measured: the first animation frame after this
  // record (which every caller writes synchronously with its ack setState)
  // is the paint that could show the outcome. Refusals and throttles are
  // also terminal at that ack unless the caller later overrides.
  scheduleRenderStamp(id);
  return id;
}

function scheduleRenderStamp(id: number): void {
  const raf =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (fn: () => void) => setTimeout(fn, 0);
  raf(() => {
    const entry = _byId.get(id);
    if (entry && entry.renderMs == null) entry.renderMs = Date.now();
  });
}

/** Patch a recorded entry with later truth (final state, corrected render). */
export function updateLt2Timing(
  id: number,
  patch: Partial<Pick<Lt2TimingEntry, 'responseMs' | 'finalMs' | 'finalOutcome' | 'outcome'>>,
): void {
  const entry = _byId.get(id);
  if (entry) Object.assign(entry, patch);
}

export function lt2TimingEntries(): readonly Lt2TimingEntry[] {
  return _entries;
}

export function _resetLt2TimingForTests(): void {
  _entries.length = 0;
  _byId.clear();
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, idx)];
}

export interface Lt2TimingSummaryRow {
  inputMethod: string;
  count: number;
  /** input → local acknowledgement (the parity number). */
  inputToAckP50Ms: number | null;
  inputToAckP95Ms: number | null;
  /** request → relay response (venue round trip). */
  requestToResponseP50Ms: number | null;
  requestToResponseP95Ms: number | null;
  /** input → first acknowledgement frame (the pending/refusal ack). */
  inputToRenderP50Ms: number | null;
  inputToRenderP95Ms: number | null;
  /** input → terminal state (see finalMs). */
  inputToFinalP50Ms: number | null;
  inputToFinalP95Ms: number | null;
}

/** Per-input-method percentiles over the entries that carry each interval. */
export function lt2TimingSummary(): Lt2TimingSummaryRow[] {
  const byMethod = new Map<string, Lt2TimingEntry[]>();
  for (const e of _entries) {
    const list = byMethod.get(e.inputMethod) ?? [];
    list.push(e);
    byMethod.set(e.inputMethod, list);
  }
  return [...byMethod.entries()].map(([inputMethod, list]) => {
    const acks = list
      .filter((e) => e.inputAtMs != null)
      .map((e) => e.ackMs - (e.inputAtMs as number))
      .sort((a, b) => a - b);
    const rtts = list
      .filter((e) => e.requestMs != null && e.responseMs != null)
      .map((e) => (e.responseMs as number) - (e.requestMs as number))
      .sort((a, b) => a - b);
    const renders = list
      .filter((e) => e.inputAtMs != null && e.renderMs != null)
      .map((e) => (e.renderMs as number) - (e.inputAtMs as number))
      .sort((a, b) => a - b);
    const finals = list
      .filter((e) => e.inputAtMs != null && e.finalMs != null)
      .map((e) => (e.finalMs as number) - (e.inputAtMs as number))
      .sort((a, b) => a - b);
    return {
      inputMethod,
      count: list.length,
      inputToAckP50Ms: percentile(acks, 50),
      inputToAckP95Ms: percentile(acks, 95),
      requestToResponseP50Ms: percentile(rtts, 50),
      requestToResponseP95Ms: percentile(rtts, 95),
      inputToRenderP50Ms: percentile(renders, 50),
      inputToRenderP95Ms: percentile(renders, 95),
      inputToFinalP50Ms: percentile(finals, 50),
      inputToFinalP95Ms: percentile(finals, 95),
    };
  });
}

declare global {
  interface Window {
    __lt2Timing?: {
      entries: () => readonly Lt2TimingEntry[];
      summary: () => Lt2TimingSummaryRow[];
    };
  }
}

// The named-trader parity session reads this from the console; it is
// read-only and session-local.
if (typeof window !== 'undefined') {
  window.__lt2Timing = { entries: lt2TimingEntries, summary: lt2TimingSummary };
}
